<template>
  <div class="dialog">
    <span class="cls" @click="oth_close()"></span>
    <div class="paper">
      <!-- 닫기 버튼 -->
      <button class="close-btn" @click="oth_close()">
        <i class="ph-bold ph-x"></i>
      </button>

      <div class="content">
        <!-- 🆕 링크 생성 완료 화면 -->
        <template v-if="linkCreated">
          <div class="link-success">
            <div class="success-icon">
              <i class="ph-fill ph-gift"></i>
            </div>
            <h3>선물 링크가 생성되었습니다!</h3>
            <p class="recipient-info">{{ recipientName }} ({{ recipientPhone }})</p>

            <!-- 링크 복사 -->
            <div class="link-box">
              <input 
                type="text" 
                :value="generatedLink" 
                readonly 
                ref="linkInput"
              />
              <button @click="copyLink" class="copy-btn">
                <i class="ph-bold ph-copy"></i>
              </button>
            </div>

            <!-- 공유 버튼들 -->
            <div class="share-buttons">
              <button @click="shareKakao" class="share-kakao">
                <i class="ph-fill ph-chat-circle"></i>
                카톡 보내기
              </button>
              <button @click="shareSMS" class="share-sms">
                <i class="ph-fill ph-device-mobile"></i>
                문자 보내기
              </button>
            </div>
          </div>
        </template>

        <!-- 보내기 모드가 아닐 때 -->
        <template v-else-if="!sendMode">
          <!-- 상품 이미지 -->
          <div class="gift-image">
            <img 
              v-if="gift.goods_img" 
              :src="gift.goods_img" 
              :alt="gift.goods_name"
            />
            <i v-else class="ph-fill ph-gift"></i>
          </div>

          <!-- 브랜드 & 상품명 -->
          <div class="gift-title">
            <span class="brand">{{ gift.brand_name }}</span>
            <h3 class="name">{{ gift.goods_name }}</h3>
          </div>

          <!-- 통계 정보 -->
          <div class="stats">
            <div class="stat-item total">
              <span class="label">총 보유수량</span>
              <strong class="value">{{ giftStats.total }}개</strong>
            </div>
            <div class="stat-item remaining">
              <span class="label">남은수량</span>
              <strong class="value">{{ giftStats.remaining }}개</strong>
            </div>
            <div class="stat-item sent">
              <span class="label">발송완료</span>
              <strong class="value">{{ giftStats.sent }}개</strong>
            </div>
          </div>

          <!-- 상세정보 (접기) -->
          <details class="detail-info" open>
            <summary>상세정보</summary>
            <div class="detail-content">
              <div class="detail-row">
                <span class="detail-label">구매 단가</span>
                <span class="detail-value">{{ formatPrice(giftStats.price) }}원</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">구매 방식</span>
                <span class="detail-value">{{ giftStats.purchaseMethod }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">교환일자</span>
                <span class="detail-value">{{ formatDate(giftStats.latestDate) }}</span>
              </div>
            </div>
          </details>
        </template>

        <!-- 보내기 모드일 때 -->
        <template v-else>
          <!-- 상품 요약 -->
          <div class="send-summary">
            <div class="summary-image">
              <img 
                v-if="gift.goods_img" 
                :src="gift.goods_img" 
                :alt="gift.goods_name"
              />
              <i v-else class="ph-fill ph-gift"></i>
            </div>
            <div class="summary-info">
              <span class="summary-brand">{{ gift.brand_name }}</span>
              <p class="summary-name">{{ gift.goods_name }}</p>
            </div>
          </div>

          <!-- 수신자 선택 -->
          <div class="recipient-section">
            <h4>선물 받을 사람</h4>
            
            <!-- 수신자 타입 선택 -->
            <div class="recipient-tabs">
              <button 
                :class="{ active: recipientType === 'self' }" 
                @click="recipientType = 'self'"
              >
                <i class="ph-bold ph-user"></i> 나에게
              </button>
              <button 
                :class="{ active: recipientType === 'customer' }" 
                @click="recipientType = 'customer'"
              >
                <i class="ph-bold ph-gift"></i> 고객에게
              </button>
            </div>

            <!-- 나에게 -->
            <div v-if="recipientType === 'self'" class="recipient-form">
              <div class="form-group">
                <label>받는 사람</label>
                <input type="text" v-model="recipientName" placeholder="이름" />
              </div>
              <div class="form-group">
                <label>휴대폰 번호</label>
                <input type="tel" v-model="recipientPhone" placeholder="010-0000-0000" />
              </div>
            </div>

            <!-- 고객에게 -->
            <div v-else class="recipient-form">
              <div class="form-group">
                <label>고객 선택</label>
                <select v-model="selectedCustomer" @change="onCustomerSelect">
                  <option :value="null">고객을 선택하세요</option>
                  <option v-for="c in customerList" :key="c.idx" :value="c">
                    {{ c.name }} ({{ c.phone }})
                  </option>
                </select>
              </div>
              <div class="form-group" v-if="selectedCustomer">
                <label>휴대폰 번호</label>
                <input type="tel" v-model="recipientPhone" readonly />
              </div>
            </div>

            <!-- 메시지 -->
            <div class="form-group">
              <label>선물 메시지 <small>(선택)</small></label>
              <textarea v-model="mmsMsg" placeholder="저의 마음을 전합니다." maxlength="200"></textarea>
            </div>
          </div>
        </template>
      </div>

      <!-- 하단 버튼 -->
      <div class="footer">
        <!-- 링크 생성 완료 시 -->
        <button 
          v-if="linkCreated" 
          class="send-btn" 
          @click="resetToInitial"
        >
          확인
        </button>

        <!-- 기본 모드 -->
        <button 
          v-else-if="!sendMode" 
          class="send-btn" 
          :disabled="giftStats.remaining === 0"
          @click="handleSend"
        >
          보내기
        </button>

        <!-- 보내기 모드 -->
        <template v-else>
          <button class="cancel-btn" @click="sendMode = false">
            취소
          </button>
          <button 
            class="confirm-btn" 
            :disabled="!canSend || sending"
            @click="handleConfirmSend"
          >
            <i class="ph-bold ph-spinner" v-if="sending"></i>
            <span v-else>{{ sendButtonText }}</span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, onMounted, computed } from 'vue';

export default {
  name: 'giftDetailDialog',
  props: {
    gift: {
      type: Object,
      required: true
    },
    oth_close: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');
    const showToast = inject('showToast', () => {});

    const giftStats = ref({
      total: 0,
      remaining: 0,
      sent: 0,
      price: 0,
      purchaseMethod: '포인트교환',
      latestDate: null
    });

    // 보내기 모드
    const sendMode = ref(false);
    const sending = ref(false);
    
    // 수신자 정보
    const recipientType = ref('self');
    const recipientName = ref('');
    const recipientPhone = ref('');
    const selectedCustomer = ref(null);
    const customerList = ref([]);
    const mmsMsg = ref('저의 마음을 전합니다.');

    // 사용 가능한 쿠폰 목록
    const availableCoupons = ref([]);

    // 🆕 링크 관련
    const linkCreated = ref(false);
    const generatedLink = ref('');
    const linkToken = ref('');
    const linkInput = ref(null);

    // 선물 상세 통계 조회
    async function fetchGiftStats() {
      try {
        const sql = `
          SELECT 
            idx,
            coupon_code,
            ktgift_product_code,
            amount,
            status,
            memo,
            issued_at
          FROM giftcoupon
          WHERE issued_by = '${member_info.mb_id}'
            AND coupon_type = '${props.gift.brand_name}'
        `;
        const res = await sql_query(sql);

        if (!res || res.length === 0) return;

        // 같은 상품명으로 필터링
        const sameGifts = res.filter(item => {
          try {
            const memoData = typeof item.memo === 'string' ? JSON.parse(item.memo) : item.memo;
            return memoData?.goods_name === props.gift.goods_name;
          } catch (e) {
            return false;
          }
        });

        const total = sameGifts.length;
        const remainingGifts = sameGifts.filter(g => ['발행', '사용대기'].includes(g.status));
        const remaining = remainingGifts.length;
        const sent = sameGifts.filter(g => {
          try {
            const memoData = typeof g.memo === 'string' ? JSON.parse(g.memo) : g.memo;
            return memoData?.recipient_type === 'customer';
          } catch (e) {
            return false;
          }
        }).length;

        // 사용 가능한 쿠폰 목록 저장
        availableCoupons.value = remainingGifts;

        // 최신 데이터에서 가격 정보 가져오기
        const latest = sameGifts[0];
        const price = latest?.amount || 0;
        const latestDate = latest?.issued_at || null;

        giftStats.value = {
          total,
          remaining,
          sent,
          price,
          purchaseMethod: '포인트교환',
          latestDate
        };
      } catch (error) {
        console.error('선물 통계 조회 실패:', error);
      }
    }

    const formatPrice = (price) => {
      return price?.toLocaleString() || '0';
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    };

    // 고객 목록 조회
    async function fetchCustomers() {
      const sql = `
        SELECT idx, name, phone FROM customers 
        WHERE mb_id = '${member_info.mb_id}' 
          AND phone IS NOT NULL AND phone != ''
        ORDER BY name ASC
      `;
      const res = await sql_query(sql);
      customerList.value = res || [];
    }

    // 고객 선택 시
    function onCustomerSelect() {
      if (selectedCustomer.value) {
        recipientName.value = selectedCustomer.value.name;
        recipientPhone.value = selectedCustomer.value.phone;
      }
    }

    // 보내기 가능 여부
    const canSend = computed(() => {
      if (!recipientPhone.value) return false;
      if (giftStats.value.remaining === 0) return false;
      return true;
    });

    // 버튼 텍스트
    const sendButtonText = computed(() => {
      if (!recipientPhone.value) return '받는 사람을 입력하세요';
      if (giftStats.value.remaining === 0) return '보유 선물 없음';
      return '링크 생성';
    });

    // 보내기 버튼 클릭
    const handleSend = async () => {
      if (giftStats.value.remaining === 0) {
        showToast('보낼 수 있는 선물이 없습니다', 'error');
        return;
      }
      
      // 고객 목록 로드
      await fetchCustomers();
      
      // 기본값 설정
      recipientName.value = member_info.name || '';
      recipientPhone.value = member_info.phone || '';
      
      sendMode.value = true;
    };

    // 🆕 토큰 생성
    function generateToken() {
      return Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    }

    // 🆕 링크 생성
    async function handleConfirmSend() {
      if (!canSend.value || sending.value) return;

      const confirmed = confirm(
        `${props.gift.goods_name}\n` +
        `받는 사람: ${recipientName.value || '미입력'}\n` +
        `전화번호: ${recipientPhone.value}\n\n` +
        `선물 링크를 생성하시겠습니까?`
      );

      if (!confirmed) return;

      sending.value = true;

      try {
        // 사용 가능한 쿠폰 확인
        if (availableCoupons.value.length === 0) {
          showToast('보낼 수 있는 선물이 없습니다', 'error');
          return;
        }

        const coupon = availableCoupons.value[0];


        // memo 데이터 파싱
        let memoData = {};
        try {
          memoData = typeof coupon.memo === 'string' ? JSON.parse(coupon.memo) : coupon.memo;
        } catch (e) {
          memoData = {};
        }

        const token = generateToken();
        linkToken.value = token;

        // memo 데이터 파싱
        // let memoData = {};
        // try {
        //   //memoData = typeof coupon.memo === 'string' ? JSON.parse(coupon.memo) : coupon.memo;
        // } catch (e) {
        //   console.log(e);
        // }

        // 🔧 상품 정보 함께 저장
        await sql_query(`
          INSERT INTO gift_links (
            token,
            coupon_idx,
            goods_code,
            sender_id,
            receiver_phone,
            receiver_name,
            gift_message,
            goods_name,
            brand_name,
            brand_img,
            goods_img,
            sale_price,
            discount_price,
            status,
            created_at,
            expires_at
          ) VALUES (
            '${token}',
            ${coupon.idx},
            '${coupon.ktgift_product_code || memoData.goods_code || ''}',
            '${member_info.mb_id}',
            '${recipientPhone.value.replace(/-/g, '')}',
            '${recipientName.value}',
            '${mmsMsg.value.replace(/'/g, "\\'")}',
            '${memoData.goods_name || props.gift.goods_name || ''}',
            '${memoData.brand_name || props.gift.brand_name || ''}',
            '${memoData.brand_img || props.gift.brand_img || ''}',
            '${memoData.goods_img || props.gift.goods_img || ''}',
            ${memoData.sale_price || coupon.amount || 0},
            ${memoData.discount_price || coupon.amount || 0},
            'pending',
            NOW(),
            DATE_ADD(NOW(), INTERVAL 7 DAY)
          )
        `);

        // 🔧 쿠폰 상태를 '제공'으로 변경 (보유 목록에서 빠짐)
        await sql_query(`
          UPDATE giftcoupon 
          SET status = '제공',
              issued_to = '${recipientName.value}(${recipientPhone.value.replace(/-/g, '')})'
          WHERE idx = ${coupon.idx}
        `);

        // 링크 생성
        generatedLink.value = `${window.location.origin}/gift/${token}`;
        linkCreated.value = true;

        showToast('선물 링크가 생성되었습니다!', 'success');

      } catch (e) {
        console.error('링크 생성 오류:', e);
        showToast(e.message || '링크 생성 중 오류 발생', 'error');
      } finally {
        sending.value = false;
      }
    }

    // 🆕 링크 복사
    async function copyLink() {
      try {
        await navigator.clipboard.writeText(generatedLink.value);
        showToast('링크가 복사되었습니다', 'success');
      } catch (e) {
        // Fallback
        if (linkInput.value) {
          linkInput.value.select();
          document.execCommand('copy');
          showToast('링크가 복사되었습니다', 'success');
        }
      }
    }

    // 🆕 카카오톡 공유
    function shareKakao() {
      const message = `[${member_info.name}] 님이 선물을 보냈습니다!\n${props.gift.goods_name}\n\n${generatedLink.value}`;
      window.open(`sms:${recipientPhone.value}?body=${encodeURIComponent(message)}`);
    }

    // 🆕 문자 공유
    function shareSMS() {
      const message = `[${member_info.name}] 님이 선물을 보냈습니다!\n${props.gift.goods_name}\n\n${generatedLink.value}`;
      window.open(`sms:${recipientPhone.value}?body=${encodeURIComponent(message)}`);
    }

    // 🆕 초기 화면으로
    function resetToInitial() {
      linkCreated.value = false;
      generatedLink.value = '';
      sendMode.value = false;
      recipientType.value = 'self';
      recipientName.value = '';
      recipientPhone.value = '';
      selectedCustomer.value = null;
      mmsMsg.value = '저의 마음을 전합니다.';
      
      // 통계 갱신
      fetchGiftStats();
    }

    onMounted(() => {
      fetchGiftStats();
    });

    return {
      giftStats,
      sendMode,
      sending,
      recipientType,
      recipientName,
      recipientPhone,
      selectedCustomer,
      customerList,
      mmsMsg,
      canSend,
      sendButtonText,
      formatPrice,
      formatDate,
      onCustomerSelect,
      handleSend,
      handleConfirmSend,
      // 🆕
      linkCreated,
      generatedLink,
      linkInput,
      copyLink,
      shareKakao,
      shareSMS,
      resetToInitial
    };
  }
}
</script>

<style scoped>
.dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999999;
}

.cls {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(4px);
  z-index: -1;
}

.paper {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 400px;
  max-height: 85vh;
  background: var(--color-background);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 닫기 버튼 */
.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-xl);
  border-radius: var(--radius-full);
  transition: all 0.2s;
  z-index: 10;
}

.close-btn:hover {
  background: var(--color-surface);
  color: var(--color-text-primary);
}

/* 컨텐츠 */
.content {
  flex: 1;
  overflow-y: auto;
  padding: 40px 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

/* 상품 이미지 */
.gift-image {
  width: 120px;
  height: 120px;
  border-radius: 20px;
  background: linear-gradient(135deg, #E0F7FA 0%, #B2EBF2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.gift-image img {
  width: 90%;
  height: 90%;
  object-fit: contain;
}

.gift-image i {
  font-size: 60px;
  color: var(--color-text-quaternary);
}

/* 브랜드 & 상품명 */
.gift-title {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.gift-title .brand {
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-medium);
}

.gift-title .name {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

/* 통계 정보 */
.stats {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

.stat-item .label {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-normal);
}

.stat-item .value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.stat-item.total .value {
  color: var(--color-text-primary);
}

.stat-item.remaining .value {
  color: var(--color-primary);
}

.stat-item.sent .value {
  color: var(--color-text-secondary);
}

/* 상세정보 */
.detail-info {
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  margin-top: 8px;
}

.detail-info summary {
  padding: 12px 16px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  cursor: pointer;
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
}

.detail-info summary::after {
  content: '▼';
  font-size: 10px;
  color: var(--color-text-tertiary);
  transition: transform 0.2s;
}

.detail-info[open] summary::after {
  transform: rotate(180deg);
}

.detail-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--color-background);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
}

.detail-value {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

/* 하단 버튼 */
.footer {
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
  display: flex;
  gap: 12px;
}

.send-btn {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #FFA726 0%, #FF9800 100%);
  color: #fff;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.send-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

.send-btn:active {
  transform: translateY(0);
}

.send-btn:disabled {
  background: var(--color-text-quaternary);
  cursor: not-allowed;
  box-shadow: none;
}

.cancel-btn,
.confirm-btn {
  flex: 1;
  padding: 14px;
  border: none;
  border-radius: 12px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn {
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
}

.cancel-btn:hover {
  background: var(--color-surface-deep);
}

.confirm-btn {
  background: linear-gradient(135deg, #FFA726 0%, #FF9800 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.confirm-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

.confirm-btn:disabled {
  background: var(--color-text-quaternary);
  cursor: not-allowed;
  box-shadow: none;
}

.confirm-btn i {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 보내기 모드 - 상품 요약 */
.send-summary {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
}

.summary-image {
  width: 60px;
  height: 60px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, #E0F7FA 0%, #B2EBF2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.summary-image img {
  width: 90%;
  height: 90%;
  object-fit: contain;
}

.summary-image i {
  font-size: 30px;
  color: var(--color-text-quaternary);
}

.summary-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-brand {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-medium);
}

.summary-name {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  line-height: 1.3;
}

/* 수신자 선택 섹션 */
.recipient-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.recipient-section h4 {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

/* 수신자 탭 */
.recipient-tabs {
  display: flex;
  gap: 8px;
}

.recipient-tabs button {
  flex: 1;
  padding: 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}

.recipient-tabs button.active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: #fff;
}

/* 폼 */
.recipient-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-group label {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.form-group label small {
  color: var(--color-text-quaternary);
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 10px 12px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  background: var(--color-surface);
  color: var(--color-text-primary);
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--color-background-light);
}

.form-group textarea {
  min-height: 60px;
  resize: none;
}

.form-group input[readonly] {
  background: var(--color-surface-deep);
  color: var(--color-text-secondary);
}

/* 🆕 링크 생성 완료 화면 */
.link-success {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.success-icon {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon i {
  font-size: 40px;
  color: #fff;
}

.link-success h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.recipient-info {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.link-box {
  width: 100%;
  display: flex;
  gap: 8px;
  padding: 12px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

.link-box input {
  flex: 1;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  font-family: monospace;
}

.copy-btn {
  padding: 8px 12px;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-brand);
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.copy-btn:hover {
  transform: scale(1.05);
}

.share-buttons {
  width: 100%;
  display: flex;
  gap: 8px;
}

.share-kakao,
.share-sms {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}

.share-kakao {
  background: #FEE500;
  color: #000;
}

.share-kakao:hover {
  background: #FFD600;
}

.share-sms {
  background: var(--color-brand);
  color: #fff;
}

.share-sms:hover {
  opacity: 0.9;
}

/* 모바일 */
@media (max-width: 480px) {
  .paper {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .content {
    padding: 50px 20px 20px;
  }
}
</style>